import { createContext, useContext } from 'react';

interface Location {
  id: string;
  name: string;
  code?: string;
  company_id?: string;
  address?: string;
  phone?: string;
  email?: string;
  timezone?: string;
  is_active?: boolean;
}

interface LocationContextType {
  currentLocation: Location | null;
  availableLocations: Location[];
  isLoadingLocation: boolean;
  canSwitchLocations: boolean;
  /** Franchisor "All locations" view (admin/super_admin only). When true
   *  the dashboard shows the fleet glance instead of a store's detail.
   *  currentLocation stays set underneath so location-scoped surfaces
   *  never see a null location. Cleared by switchLocation(). */
  isAllLocations: boolean;
  setAllLocationsView?: (on: boolean) => void;
  setCurrentLocation?: (location: Location) => void;
  switchLocation?: (locationId: string) => Promise<void>;
  refreshLocations: () => Promise<void>;
}

// SHARED context used by both LocationProvider and LimitedLocationProvider
export const SharedLocationContext = createContext<LocationContextType | undefined>(undefined);

// Shared hook that works with either provider
export const useLocation = () => {
  const context = useContext(SharedLocationContext);
  if (context === undefined) {
    // Return safe defaults for components that render before provider is ready
    console.warn('[LOCATION_HOOK] useLocation called outside of provider - returning defaults');
    return {
      currentLocation: null,
      availableLocations: [],
      isLoadingLocation: false,
      canSwitchLocations: false,
      isAllLocations: false,
      setAllLocationsView: () => {},
      setCurrentLocation: () => {},
      switchLocation: async () => {},
      refreshLocations: async () => {}
    };
  }
  return context;
};
